import {
  IconAdd,
  IconSettings,
  InlineTextButton,
  type ISelectProps,
  Select,
  Space,
  SubText,
} from '@procurify/ui'

import { BridgeLink } from '@webapp/core/components/BridgeLink'
import { useProcurifyIntl } from '@webapp/hooks'
import { type CreditCardProviderOption } from './CreditCardProviderOption'
import messages from './messages'
import { useCreditCardProviderSelect } from './useCreditCardProviderSelect'

// Angular state `ap.payee-management.cc-providers`. With FT_4280 on, it opens
// the first provider, whose Angular state redirects to the React
// /payments/manage-payees/credit-card-providers/:id/payment-methods page, so
// this hash link is right in both flag states.
const CREDIT_CARD_PROVIDERS_MANAGEMENT_URL =
  '/#/ap/payee-management/cc-providers'

// react-select's "Create" option is never selected (onCreateOption takes the
// choice), so its id only has to differ from every real provider id.
const CREATE_OPTION_ID = -1

export interface ICreditCardProviderSelectProps extends ISelectProps<CreditCardProviderOption> {
  /**
   * Entitlement-derived (ap.everythingButExpense) and provided by the
   * consumer — the select never reads entitlements itself (BHV-019).
   */
  showManageProviders?: boolean
  /**
   * Shows the sticky "Create new" action, plus a keyboard-reachable
   * "Create "<term>"" option for a search term that matches no provider, and
   * receives the typed search term (BHV-018). The consumer renders
   * CreditCardProviderInlineCreate after the select's form control, so the
   * field error stays under the select.
   */
  onCreateNew?: (searchTerm: string) => void
}

/**
 * Self-contained credit-card-provider typeahead (ADR-3 shared component, not
 * a feature). Options are type-6 vendors from
 * GET /api/v3/vendors/credit-card-providers/.
 */
export const CreditCardProviderSelect = ({
  showManageProviders = false,
  onCreateNew,
  ...props
}: ICreditCardProviderSelectProps) => {
  const intl = useProcurifyIntl()

  const {
    data: { providers },
    states: { isFetching },
  } = useCreditCardProviderSelect()

  const hasStickies = Boolean(onCreateNew) || showManageProviders

  return (
    <Select<CreditCardProviderOption>
      options={providers}
      isLoading={isFetching}
      getOptionLabel={(option) => option.name}
      getOptionValue={(option) => option.id.toString()}
      placeholder={intl.formatMessage(messages.placeholder)}
      aria-label={intl.formatMessage(messages.ariaLabel)}
      creatable={Boolean(onCreateNew)}
      getNewOptionData={(searchTerm) => ({
        id: CREATE_OPTION_ID,
        name: intl.formatMessage(messages.createOption, { searchTerm }),
      })}
      onCreateOption={onCreateNew}
      stickies={
        hasStickies
          ? ({ closeMenu, selectProps }) => {
              const stickies: JSX.Element[] = []

              if (onCreateNew) {
                stickies.push(
                  <Select.StickyOption key='create-new'>
                    <InlineTextButton
                      type='button'
                      onClick={() => {
                        // Read the typed search term before closeMenu clears it.
                        onCreateNew(selectProps.inputValue)
                        closeMenu()
                      }}
                    >
                      <Space
                        direction='horizontal'
                        size='xsmall'
                        align='center'
                      >
                        <IconAdd size={20} />
                        <SubText bold>
                          {intl.formatMessage(messages.createNewOption)}
                        </SubText>
                      </Space>
                    </InlineTextButton>
                  </Select.StickyOption>
                )
              }

              if (showManageProviders) {
                stickies.push(
                  <Select.StickyOption key='manage-providers'>
                    <BridgeLink to={CREDIT_CARD_PROVIDERS_MANAGEMENT_URL}>
                      <Space
                        direction='horizontal'
                        size='xsmall'
                        align='center'
                      >
                        <IconSettings size={20} />
                        <SubText bold>
                          {intl.formatMessage(messages.manageProvidersOption)}
                        </SubText>
                      </Space>
                    </BridgeLink>
                  </Select.StickyOption>
                )
              }

              return stickies
            }
          : undefined
      }
      {...props}
    />
  )
}

CreditCardProviderSelect.displayName = 'CreditCardProviderSelect'
